import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { abrirOuDescarregar } from '../utils/abrirFicheiro';

/* ============================================================
   SISTEMA INTEGRADO (SGI)
   Não Conformidades / Ações — P_GSI_03
   Registo e tratamento de NC/OM: identificação → análise de causas →
   ação (correção / ação corretiva / ação de melhoria) → comunicação →
   avaliação da eficácia. Ligação opcional a um orçamento e anexos
   (reclamação, resposta e evidências).
   ============================================================ */

const TIPO_INFO = {
    nc: { label: 'NC', full: 'Não Conformidade', bg: '#fee2e2', fg: '#991b1b' },
    om: { label: 'OM', full: 'Oportunidade de Melhoria', bg: '#e0f2fe', fg: '#075985' },
};

const ORIGENS = [
    { v: 'incidente', l: 'Registo de incidente' },
    { v: 'consulta', l: 'Consulta a trabalhadores' },
    { v: 'auditoria_interna', l: 'Auditoria interna' },
    { v: 'auditoria_externa', l: 'Auditoria externa' },
    { v: 'auditoria_cliente', l: 'Auditoria de cliente' },
    { v: 'avaliacao_legal', l: 'Avaliação de conformidade legal' },
    { v: 'reclamacao_cliente', l: 'Reclamação de cliente' },
    { v: 'outro', l: 'Outro' },
];
const ORIGEM_LABEL = Object.fromEntries(ORIGENS.map(o => [o.v, o.l]));

const ESTADO_INFO = {
    aberta: { label: 'Aberta', bg: '#fef3c7', fg: '#92400e' },
    em_tratamento: { label: 'Em tratamento', bg: '#dbeafe', fg: '#1e40af' },
    fechada: { label: 'Fechada', bg: '#dcfce7', fg: '#166534' },
};
const EFICACIA_INFO = {
    pendente: { label: 'Pendente', bg: '#f1f5f9', fg: '#475569' },
    eficaz: { label: 'Eficaz', bg: '#dcfce7', fg: '#166534' },
    nao_eficaz: { label: 'Não eficaz', bg: '#fee2e2', fg: '#991b1b' },
};
const CATEGORIAS = [
    { v: 'reclamacao', l: 'Reclamação', icon: '📩', hint: 'Documento(s) da reclamação recebida' },
    { v: 'resposta', l: 'Resposta', icon: '↩️', hint: 'Resposta / comunicação enviada' },
    { v: 'evidencia', l: 'Evidências', icon: '📎', hint: 'Outros documentos de suporte' },
];

const toDateInput = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    try {
        const d = new Date(v);
        const d2 = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        return d2.toISOString().slice(0, 10);
    } catch { return ''; }
};
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '—');
const fmtBytes = (n) => {
    const b = parseInt(n, 10) || 0;
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/* ---------- Seletor de orçamento (pesquisa) ---------- */
function OrcamentoPicker({ orcamentoId, orcamentoLabel, onPick }) {
    const [q, setQ] = useState('');
    const [resultados, setResultados] = useState([]);
    const [aberto, setAberto] = useState(false);
    const [busy, setBusy] = useState(false);

    const pesquisar = async (termo) => {
        setQ(termo);
        if (!termo || termo.trim().length < 1) { setResultados([]); setAberto(false); return; }
        setBusy(true);
        try {
            const res = await api.get('/orcamentos', { params: { search: termo.trim() } });
            setResultados((res.data || []).slice(0, 12));
            setAberto(true);
        } catch { setResultados([]); } finally { setBusy(false); }
    };

    if (orcamentoId) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span data-liquid-tone={liquidTone('#eef2ff')} data-liquid-badge="true" style={{ ...badge, background: '#eef2ff', color: '#3730a3' }}>
                    #{orcamentoId}{orcamentoLabel ? ` · ${orcamentoLabel}` : ''}
                </span>
                <button data-liquid-secondary="true" type="button" onClick={() => { onPick(null, ''); setQ(''); }} style={{ ...btnSec, padding: '4px 10px' }}>
                    Desassociar
                </button>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative' }}>
            <input
                value={q}
                onChange={e => pesquisar(e.target.value)}
                onFocus={() => resultados.length && setAberto(true)}
                placeholder="Pesquisar orçamento por nº, cliente ou projeto…"
                style={{ ...inp, width: '100%' }}
            />
            {busy && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>A pesquisar…</div>}
            {aberto && resultados.length > 0 && (
                <div style={dropdown}>
                    {resultados.map(o => (
                        <div key={o.id} style={dropdownItem}
                            onClick={() => {
                                onPick(o.id, `${o.projeto || 'Sem projeto'}${o.cliente ? ` — ${o.cliente}` : ''}`);
                                setAberto(false); setQ('');
                            }}>
                            <strong>#{o.id}</strong> · {o.projeto || 'Sem projeto'}
                            {o.cliente && <span style={{ color: '#64748b' }}> — {o.cliente}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------- Bloco de anexos por categoria ---------- */
function AnexoBloco({ ncId, categoria, label, icon, hint, anexos, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const lista = anexos.filter(a => a.categoria === categoria);

    const carregar = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;
        setBusy(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('categoria', categoria);
            files.forEach(f => fd.append('files', f));
            await api.post(`/nao-conformidades/${ncId}/anexos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            onChanged();
        } catch (e2) {
            setErr(e2.response?.data?.error || 'Erro ao carregar ficheiro');
        } finally { setBusy(false); }
    };

    const baixar = async (a) => {
        try {
            const res = await api.get(`/nao-conformidades/anexos/${a.id}`, { responseType: 'blob' });
            abrirOuDescarregar(res, a.nome_original || 'anexo');
        } catch { alert('Erro ao descarregar ficheiro'); }
    };

    const remover = async (a) => {
        if (!window.confirm(`Remover "${a.nome_original}"?`)) return;
        try { await api.delete(`/nao-conformidades/anexos/${a.id}`); onChanged(); }
        catch { alert('Erro ao remover ficheiro'); }
    };

    return (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div>
                    <strong style={{ fontSize: '0.9rem' }}><LiquidText>{icon}</LiquidText> <LiquidText>{label}</LiquidText></strong>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{hint}</div>
                </div>
                <label style={{ ...btnSec, padding: '5px 10px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                    {busy ? 'A carregar…' : '＋ Ficheiro'}
                    <input type="file" multiple onChange={carregar} disabled={busy} style={{ display: 'none' }} />
                </label>
            </div>
            {err && <div style={{ color: '#e53e3e', fontSize: '0.78rem', marginBottom: 6 }}>{err}</div>}
            {lista.length === 0 ? (
                <div style={{ fontSize: '0.78rem', color: '#cbd5e1', padding: '4px 0' }}>Sem ficheiros.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {lista.map(a => (
                        <div data-liquid-actions="true" key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem' }}>
                            <button type="button" onClick={() => baixar(a)}
                                style={{ ...linkBtn, flex: 1, textAlign: 'left' }} title="Descarregar"><LiquidText>
                                📄 </LiquidText>{a.nome_original}
                            </button>
                            <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{fmtBytes(a.tamanho)}</span>
                            <button type="button" onClick={() => remover(a)} style={{ ...linkBtn, color: '#dc2626' }} title="Remover"><DeleteIcon /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------- Modal de NC ---------- */
function ModalNC({ nc, onClose, onSaved }) {
    const novo = !nc?.id;
    const [savedId, setSavedId] = useState(nc?.id || null);
    const [codigo, setCodigo] = useState(nc?.codigo || null);
    const [anexos, setAnexos] = useState(nc?.anexos || []);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [orcLabel, setOrcLabel] = useState(
        nc?.orcamento_id ? `${nc.orcamento_projeto || 'Sem projeto'}${nc.orcamento_cliente ? ` — ${nc.orcamento_cliente}` : ''}` : ''
    );

    const [form, setForm] = useState({
        tipo: nc?.tipo || 'nc',
        origem: nc?.origem || '',
        data_deteccao: toDateInput(nc?.data_deteccao) || toDateInput(new Date()),
        orcamento_id: nc?.orcamento_id || null,
        cliente: nc?.cliente || '',
        titulo: nc?.titulo || '',
        descricao: nc?.descricao || '',
        analise_causas: nc?.analise_causas || '',
        correcao: nc?.correcao || '',
        acao_corretiva: nc?.acao_corretiva || '',
        acao_melhoria: nc?.acao_melhoria || '',
        resposta: nc?.resposta || '',
        estado: nc?.estado || 'aberta',
        prazo: toDateInput(nc?.prazo),
        eficacia: nc?.eficacia || 'pendente',
        data_verificacao: toDateInput(nc?.data_verificacao),
        responsavel: nc?.responsavel || '',
    });
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const recarregarAnexos = useCallback(async () => {
        if (!savedId) return;
        try { const r = await api.get(`/nao-conformidades/${savedId}`); setAnexos(r.data.anexos || []); }
        catch { /* ignora */ }
    }, [savedId]);

    const guardar = async () => {
        if (!form.titulo.trim() && !form.descricao.trim()) {
            setErr('Indique pelo menos um título ou uma descrição.'); return;
        }
        /* Sem data-limite, o tratamento fica órfão: não entra nas Validades da
           área e o atraso só se descobre quando alguém abre a NC. Uma já fechada
           não precisa — registar uma NC antiga, tratada, não pede um prazo que já
           não serve. O servidor recusa na mesma; isto é para não ser preciso
           esperar pela resposta. */
        if (form.estado !== 'fechada' && !form.prazo) {
            setErr(form.tipo === 'om'
                ? 'Indique o prazo para concretizar esta oportunidade de melhoria.'
                : 'Indique o prazo para tratar esta não conformidade.');
            return;
        }
        setSaving(true); setErr('');
        try {
            if (savedId) {
                await api.put(`/nao-conformidades/${savedId}`, form);
                onSaved();
                onClose();
            } else {
                const res = await api.post('/nao-conformidades', form);
                setSavedId(res.data.id);        // passa a modo edição para permitir anexar ficheiros
                setCodigo(res.data.codigo);
                onSaved();
            }
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar');
        } finally { setSaving(false); }
    };

    const tipoInfo = TIPO_INFO[form.tipo];

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={modal} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                        <h3 style={{ margin: 0 }}>
                            {novo && !savedId ? 'Nova Não Conformidade' : `${codigo || 'NC'} · Tratamento`}
                        </h3>
                        <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 2 }}>
                            Registo e tratamento de NC/OM — P_GSI_03
                        </div>
                    </div>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                {/* Tipo + Origem + Data */}
                <div style={row3}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                            {Object.entries(TIPO_INFO).map(([k, v]) => {
                                const sel = form.tipo === k;
                                return (
                                    <button data-liquid-choice="true" aria-pressed={sel} key={k} type="button" onClick={() => set('tipo', k)} title={v.full}
                                        style={{
                                            flex: 1, padding: '7px 6px', borderRadius: 8, cursor: 'pointer',
                                            border: sel ? `1px solid ${v.fg}` : '1px solid #e2e8f0',
                                            background: sel ? v.bg : '#fff', color: sel ? v.fg : '#64748b',
                                            fontWeight: 600, fontSize: '0.8rem',
                                        }}><LiquidText>{v.label}</LiquidText></button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Origem</label>
                        <select value={form.origem} onChange={e => set('origem', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">—</option>
                            {ORIGENS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data de deteção</label>
                        <input type="date" value={form.data_deteccao} onChange={e => set('data_deteccao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>

                {/* Orçamento + Cliente */}
                <div style={row2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Orçamento associado (opcional)</label>
                        <OrcamentoPicker
                            orcamentoId={form.orcamento_id}
                            orcamentoLabel={orcLabel}
                            onPick={(id, label) => { set('orcamento_id', id); setOrcLabel(label); }}
                        />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Cliente</label>
                        <input value={form.cliente} onChange={e => set('cliente', e.target.value)} placeholder="Nome do cliente" style={{ ...inp, width: '100%' }} />
                    </div>
                </div>

                {/* Título */}
                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Título</label>
                    <input value={form.titulo} onChange={e => set('titulo', e.target.value)} placeholder="Resumo da não conformidade" style={{ ...inp, width: '100%' }} />
                </div>

                {/* Descrição / Reclamação */}
                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Descrição da NC / Reclamação</label>
                    <textarea value={form.descricao} rows={3} onChange={e => set('descricao', e.target.value)}
                        placeholder="Descrição do desvio, incidente ou conteúdo da reclamação do cliente…" style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {/* Análise de causas */}
                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Análise das causas</label>
                    <textarea value={form.analise_causas} rows={2} onChange={e => set('analise_causas', e.target.value)}
                        placeholder="Investigação / determinação da(s) causa(s) raiz…" style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {/* Ações */}
                <div style={row3}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Correção (imediata)</label>
                        <textarea value={form.correcao} rows={3} onChange={e => set('correcao', e.target.value)}
                            placeholder="Ação para corrigir a NC" style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ação corretiva</label>
                        <textarea value={form.acao_corretiva} rows={3} onChange={e => set('acao_corretiva', e.target.value)}
                            placeholder="Eliminar a causa da NC" style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ação de melhoria</label>
                        <textarea value={form.acao_melhoria} rows={3} onChange={e => set('acao_melhoria', e.target.value)}
                            placeholder="Promover a melhoria contínua" style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>
                </div>

                {/* Resposta / comunicação */}
                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Resposta / Comunicação ao cliente</label>
                    <textarea value={form.resposta} rows={3} onChange={e => set('resposta', e.target.value)}
                        placeholder="Decisão da SPROD e ação comunicada ao cliente (no caso de reclamações)…" style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {/* Estado / prazo / eficácia / verificação / responsável */}
                <div style={{ ...rowWrap, marginTop: 12 }}>
                    <div style={{ flex: '1 1 140px' }}>
                        <label data-liquid-label="true" style={lbl}>Estado</label>
                        <select value={form.estado} onChange={e => set('estado', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(ESTADO_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                        <label data-liquid-label="true" style={lbl}>
                            Prazo{form.estado !== 'fechada' && <span style={{ color: '#d03b3b' }} title="Obrigatório enquanto não estiver fechada"> *</span>}
                        </label>
                        <input type="date" value={form.prazo} onChange={e => set('prazo', e.target.value)}
                            required={form.estado !== 'fechada'}
                            title={form.estado !== 'fechada' ? 'Data-limite para tratar. Obrigatório enquanto a NC não estiver fechada.' : 'Data-limite que tinha sido definida.'}
                            style={form.estado !== 'fechada' && !form.prazo
                                ? { ...inp, width: '100%', border: '1px solid #fca5a5', background: '#fff7f7' }
                                : { ...inp, width: '100%' }} />
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                        <label data-liquid-label="true" style={lbl}>Eficácia</label>
                        <select value={form.eficacia} onChange={e => set('eficacia', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(EFICACIA_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                        <label data-liquid-label="true" style={lbl}>Data verificação eficácia</label>
                        <input type="date" value={form.data_verificacao} onChange={e => set('data_verificacao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div style={{ flex: '1 1 160px' }}>
                        <label data-liquid-label="true" style={lbl}>Responsável (RSI)</label>
                        <input value={form.responsavel} onChange={e => set('responsavel', e.target.value)} placeholder="Nome" style={{ ...inp, width: '100%' }} />
                    </div>
                </div>

                {/* Anexos */}
                <div style={{ marginTop: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <strong style={{ fontSize: '0.95rem' }}>Ficheiros</strong>
                        <span data-liquid-tone={liquidTone(tipoInfo.bg)} data-liquid-badge="true" style={{ ...badge, background: tipoInfo.bg, color: tipoInfo.fg }}><LiquidText>{tipoInfo.label}</LiquidText></span>
                    </div>
                    {!savedId ? (
                        <div data-liquid-notice="true" data-liquid-tone={liquidTone('#fffbeb')} style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '10px 14px', borderRadius: 8, fontSize: '0.82rem' }}>
                            Guarde a NC para poder anexar a reclamação, a resposta e outras evidências.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                            {CATEGORIAS.map(c => (
                                <AnexoBloco key={c.v} ncId={savedId} categoria={c.v} label={c.l} icon={c.icon} hint={c.hint}
                                    anexos={anexos} onChanged={recarregarAnexos} />
                            ))}
                        </div>
                    )}
                </div>

                {err && <div style={{ color: '#e53e3e', margin: '12px 0 0', fontSize: '0.85rem' }}>{err}</div>}

                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>{savedId && novo ? 'Concluído' : 'Cancelar'}</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'A guardar…' : (savedId ? 'Guardar alterações' : 'Guardar NC')}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Página / lista ---------- */
const NaoConformidades = () => {
    const { user } = useAuth();
    const anoAtual = new Date().getFullYear();

    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    /* Abre com todas as que estão em aberto, de qualquer ano. Abria no ano
       corrente: em setembro de 2026 as 23 abertas eram todas de 2025 e a entrada
       dizia "0 em aberto" — uma NC por tratar não deixa de o estar por ter
       mudado o ano. O filtro por ano continua lá para quem o quiser. */
    const [ano, setAno] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState('em_aberto');
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null); // objeto NC (edição) | {} (nova) | null

    const carregar = useCallback(async () => {
        setLoading(true); setErro(null);
        try {
            const params = {};
            if (ano) params.ano = ano;
            // "Em aberto" junta dois estados, e o servidor só filtra por um:
            // pede-se tudo e tiram-se aqui as fechadas.
            if (estadoFiltro && estadoFiltro !== 'em_aberto') params.estado = estadoFiltro;
            if (search.trim()) params.search = search.trim();
            const res = await api.get('/nao-conformidades', { params });
            const dados = res.data || [];
            setLista(estadoFiltro === 'em_aberto' ? dados.filter(n => n.estado !== 'fechada') : dados);
        } catch (err) {
            console.error('Erro ao carregar não conformidades:', err);
            setErro('Não foi possível carregar as não conformidades.');
        } finally { setLoading(false); }
    }, [ano, estadoFiltro, search]);

    useEffect(() => { carregar(); }, [carregar]);

    const abrirEdicao = async (nc) => {
        try { const r = await api.get(`/nao-conformidades/${nc.id}`); setModal(r.data); }
        catch { setModal(nc); }
    };

    const eliminar = async (nc) => {
        if (!window.confirm(`Eliminar ${nc.codigo || 'esta NC'}? Os ficheiros associados serão removidos.`)) return;
        try { await api.delete(`/nao-conformidades/${nc.id}`); carregar(); }
        catch (e) { alert(e.response?.data?.error || 'Erro ao eliminar'); }
    };

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 6; a--) anos.push(a);

    const abertas = lista.filter(n => n.estado !== 'fechada').length;

    return (
        <div>
            {/* Barra de controlo */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                    <label data-liquid-label="true" style={lbl}>Ano</label>
                    <select value={ano} onChange={e => setAno(e.target.value ? parseInt(e.target.value, 10) : '')} style={inp}>
                        <option value="">Todos os anos</option>
                        {anos.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <div>
                    <label data-liquid-label="true" style={lbl}>Estado</label>
                    <select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)} style={inp}>
                        <option value="">Todos</option>
                        <option value="em_aberto">Em aberto (aberta ou em tratamento)</option>
                        {Object.entries(ESTADO_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                    <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Código, título, cliente…" style={{ ...inp, width: '100%' }} />
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                        {lista.length} registo(s) · <strong style={{ color: abertas ? '#b45309' : '#166534' }}>{abertas} em aberto</strong>
                    </span>
                    <button data-liquid-primary="true" onClick={() => setModal({})} style={btnPri}>＋ Nova NC</button>
                </div>
            </div>

            {loading ? (
                <p style={{ color: '#718096' }}>A carregar…</p>
            ) : erro ? (
                <p style={{ color: '#e53e3e' }}>{erro}</p>
            ) : lista.length === 0 ? (
                <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                    {estadoFiltro === 'em_aberto'
                        ? `Nenhuma não conformidade em aberto${ano ? ` de ${ano}` : ''}.`
                        : `Nenhuma não conformidade registada${ano ? ` em ${ano}` : ''}.`}<br />
                    <span style={{ fontSize: '0.85rem' }}>Use “＋ Nova NC” para registar a primeira.</span>
                </div>
            ) : (
                <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                    <table className="standard-table">
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Título / Origem</th>
                                <th>Cliente / Orçamento</th>
                                <th>Deteção</th>
                                <th>Prazo</th>
                                <th style={{ textAlign: 'center' }}>Estado</th>
                                <th style={{ textAlign: 'center' }}>Eficácia</th>
                                <th style={{ textAlign: 'center' }}>Anexos</th>
                                <th style={{ textAlign: 'right' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lista.map(n => {
                                const ti = TIPO_INFO[n.tipo] || TIPO_INFO.nc;
                                const ei = ESTADO_INFO[n.estado] || ESTADO_INFO.aberta;
                                const efi = EFICACIA_INFO[n.eficacia] || EFICACIA_INFO.pendente;
                                return (
                                    <tr key={n.id}>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <span data-liquid-tone={liquidTone(ti.bg)} data-liquid-badge="true" style={{ ...badge, background: ti.bg, color: ti.fg, marginRight: 6 }}><LiquidText>{ti.label}</LiquidText></span>
                                            <strong>{n.codigo}</strong>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{n.titulo || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
                                            {n.origem && <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{ORIGEM_LABEL[n.origem] || n.origem}</div>}
                                        </td>
                                        <td>
                                            {n.cliente || <span style={{ color: '#cbd5e1' }}>—</span>}
                                            {n.orcamento_id && (
                                                <div style={{ fontSize: '0.78rem', color: '#6366f1' }}>
                                                    #{n.orcamento_id}{n.orcamento_projeto ? ` · ${n.orcamento_projeto}` : ''}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(n.data_deteccao)}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(n.prazo)}</td>
                                        <td style={{ textAlign: 'center' }}><span data-liquid-tone={liquidTone(ei.bg)} data-liquid-badge="true" style={{ ...badge, background: ei.bg, color: ei.fg }}><LiquidText>{ei.label}</LiquidText></span></td>
                                        <td style={{ textAlign: 'center' }}><span data-liquid-tone={liquidTone(efi.bg)} data-liquid-badge="true" style={{ ...badge, background: efi.bg, color: efi.fg }}><LiquidText>{efi.label}</LiquidText></span></td>
                                        <td style={{ textAlign: 'center' }}>
                                            {parseInt(n.num_anexos, 10) > 0
                                                ? <span title={`${n.num_anexos} ficheiro(s)`}><LiquidText>📎 </LiquidText>{n.num_anexos}</span>
                                                : <span style={{ color: '#cbd5e1' }}>—</span>}
                                        </td>
                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <button data-liquid-secondary="true" onClick={() => abrirEdicao(n)} style={{ ...btnSec, marginRight: 6 }}>Abrir</button>
                                            <button data-liquid-secondary="true" onClick={() => eliminar(n)} style={{ ...btnSec, color: '#dc2626', borderColor: '#fecaca' }}><DeleteIcon /> Eliminar</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {modal && (
                <ModalNC
                    nc={modal.id ? modal : null}
                    onClose={() => setModal(null)}
                    onSaved={carregar}
                />
            )}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const linkBtn = { border: 'none', background: 'transparent', cursor: 'pointer', color: '#2563eb', padding: 0, fontSize: '0.82rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 860, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 };
const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 };
const rowWrap = { display: 'flex', gap: 12, flexWrap: 'wrap' };
const dropdown = { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, maxHeight: 260, overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.12)' };
const dropdownItem = { padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f1f5f9' };

export default NaoConformidades;
